import {
    ComponentPropsWithoutRef,
    ForwardedRef,
    createContext,
    forwardRef,
    useContext,
    useMemo,
} from 'react';
import { Button } from '@givebutter/primitives/button';
import { Checkbox } from '@givebutter/primitives/checkbox';
import * as Draggable from '@givebutter/primitives/draggable';
import * as Sortable from '@givebutter/primitives/sortable';
import { faEllipsisVertical, faGripDotsVertical, faPlus } from '@fortawesome/pro-regular-svg-icons';
import {
    faChevronDown,
    faChevronRight,
    faMagnifyingGlass,
    faPeopleGroup,
    faTrashCan,
    faUser,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    DropTarget,
    UNSEATED_GROUP_ID,
} from 'assets/js/pages/campaign-settings/campaign-event/table-seating/constants';
import { useSeatDetails } from 'assets/js/pages/campaign-settings/campaign-event/table-seating/hooks/useSeatDetails';
import { useSelectionOperations } from 'assets/js/pages/campaign-settings/campaign-event/table-seating/hooks/useSelectionOperation';
import { useTableOperations } from 'assets/js/pages/campaign-settings/campaign-event/table-seating/hooks/useTableOperations';
import { useTableSeatingStore } from 'assets/js/pages/campaign-settings/campaign-event/table-seating/store';
import {
    GroupPosition,
    SeatingTicket,
    TransformedSeatingGroup,
} from 'assets/js/pages/campaign-settings/campaign-event/table-seating/types';
import {
    createSeatNumberArray,
    getContiguousGroupMembersFromSeat,
    indexToSeatNumber,
} from 'assets/js/pages/campaign-settings/campaign-event/table-seating/utils/helpers';
import { twMerge } from 'tailwind-merge';
import { SeatingGroup, TicketGroup } from 'types/domain';

interface SeatingGroupCardContextValue {
    group: SeatingGroup;
    transformedGroup: TransformedSeatingGroup;
    normalizedId?: string;
    isUnseated: boolean;
}

const SeatingGroupCardContext = createContext<SeatingGroupCardContextValue | undefined>(undefined);

const useSeatingGroupCard = () => {
    const context = useContext(SeatingGroupCardContext);
    if (!context) {
        throw new Error('useSeatingGroupCard must be used within SeatingGroupCard.Root');
    }
    return context;
};

interface SeatingGroupCardContainerProps extends ComponentPropsWithoutRef<'div'> {
    group: TransformedSeatingGroup;
    normalizedId?: string;
}
const SeatingGroupCardContainer = forwardRef(
    (
        { className, group, normalizedId, children, ...restProps }: SeatingGroupCardContainerProps,
        forwardedRef: ForwardedRef<HTMLDivElement>,
    ) => {
        const formattedGroup = useMemo(
            () =>
                ({
                    id: parseInt(group.id) || 0,
                    name: group.name,
                    capacity: group.capacity == null ? undefined : group.capacity.toString(),
                    number: group.number,
                    locked: group.locked,
                    event: null,
                    tickets: null,
                }) as SeatingGroup,
            [group],
        );

        const isUnseated = normalizedId === UNSEATED_GROUP_ID;

        const contextValue = useMemo(
            () => ({
                group: formattedGroup,
                transformedGroup: group,
                normalizedId,
                isUnseated,
            }),
            [formattedGroup, group, normalizedId, isUnseated],
        );

        return (
            <SeatingGroupCardContext.Provider value={contextValue}>
                <div
                    className={twMerge(
                        'h-fit w-80 flex-shrink-0',
                        !isUnseated && 'rounded-lg border border-gray-200 bg-white shadow-sm',
                        className,
                    )}
                    ref={forwardedRef}
                    {...restProps}
                >
                    {children}
                </div>
            </SeatingGroupCardContext.Provider>
        );
    },
);
SeatingGroupCardContainer.displayName = 'SeatingGroupCardContainer';

interface SeatingGroupCardInnerProps extends ComponentPropsWithoutRef<'div'> {}
const SeatingGroupCardInner = forwardRef(
    (
        { className, children, ...restProps }: SeatingGroupCardInnerProps,
        forwardedRef: ForwardedRef<HTMLDivElement>,
    ) => {
        const { isUnseated, normalizedId } = useSeatingGroupCard();

        const { setNodeRef, isOver } = Draggable.useDroppable({
            id: normalizedId || 'dummy-droppable',
            data: {
                type: DropTarget.SEATING_CONTAINER,
                tableId: normalizedId,
            },
            disabled: !normalizedId || !isUnseated,
        });

        if (isUnseated && normalizedId) {
            return (
                <div
                    className={twMerge(
                        'flex h-full flex-col py-2',
                        isOver && 'bg-blue-50',
                        className,
                    )}
                    ref={node => {
                        setNodeRef(node);
                        if (forwardedRef) {
                            if (typeof forwardedRef === 'function') {
                                forwardedRef(node);
                            } else {
                                forwardedRef.current = node;
                            }
                        }
                    }}
                    {...restProps}
                >
                    {children}
                </div>
            );
        }

        return (
            <div
                className={twMerge(
                    'flex flex-col',
                    !isUnseated && 'rounded-lg p-3 shadow-inner',
                    isUnseated && 'h-full py-2',
                    className,
                )}
                ref={forwardedRef}
                {...restProps}
            >
                {children}
            </div>
        );
    },
);
SeatingGroupCardInner.displayName = 'SeatingGroupCardInner';

interface SeatingGroupCardHeaderProps extends ComponentPropsWithoutRef<'div'> {}
const SeatingGroupCardHeader = forwardRef(
    (
        { className, ...restProps }: SeatingGroupCardHeaderProps,
        forwardedRef: ForwardedRef<HTMLDivElement>,
    ) => {
        const { transformedGroup, normalizedId } = useSeatingGroupCard();
        const tickets = useTableSeatingStore(state => state.tickets);

        const tableTickets = useMemo(() => {
            if (!normalizedId || !tickets) return [];
            return Object.values(tickets)
                .filter(t => t.seating_group_id === normalizedId)
                .sort((a, b) => (a.seat_number || 0) - (b.seat_number || 0));
        }, [tickets, normalizedId]);

        const occupiedSeatsCount = tableTickets.length;

        return (
            <div
                className={twMerge(
                    'flex items-center justify-between border-b border-dotted border-gray-200 pb-3 pl-2',
                    className,
                )}
                ref={forwardedRef}
                {...restProps}
            >
                <div className="flex items-center gap-3">
                    <span className="font-medium text-black">{transformedGroup.number}</span>
                    <span
                        className={twMerge(
                            'text-sm font-medium',
                            transformedGroup.name === 'Add name table'
                                ? 'text-gray-500'
                                : 'text-gray-600',
                        )}
                    >
                        {transformedGroup.name}
                    </span>
                </div>
                <div className="flex items-center">
                    <div className="flex items-center gap-1">
                        <FontAwesomeIcon
                            icon={faUser}
                            size="xs"
                            className="text-brand-violet-primary"
                            aria-hidden="true"
                        />
                        <span className="text-xs text-gray-700">
                            <span className="font-medium">{occupiedSeatsCount}</span>
                            <span className="text-gray-400">/</span>
                            <span className="font-medium">{transformedGroup.capacity}</span>
                        </span>
                    </div>
                    <SeatingGroupCardHeaderMenu />
                </div>
            </div>
        );
    },
);
SeatingGroupCardHeader.displayName = 'SeatingGroupCardHeader';

const SeatingGroupCardHeaderMenu = () => {
    const { normalizedId } = useSeatingGroupCard();
    const { removeTable, canRemoveTable } = useTableOperations(normalizedId || '');

    const handleRemoveTable = () => {
        removeTable();
    };

    return (
        <Button
            variant="none"
            size="sm"
            shape="square"
            className="text-gray-500 hover:text-gray-700"
            onClick={handleRemoveTable}
            disabled={!normalizedId || !canRemoveTable}
            aria-label="Table options menu"
        >
            <FontAwesomeIcon icon={faEllipsisVertical} />
        </Button>
    );
};

interface SeatingGroupCardSeatsProps extends ComponentPropsWithoutRef<'div'> {}
const SeatingGroupCardSeats = forwardRef(
    (
        { className, children, ...restProps }: SeatingGroupCardSeatsProps,
        forwardedRef: ForwardedRef<HTMLDivElement>,
    ) => {
        const { transformedGroup, normalizedId, isUnseated } = useSeatingGroupCard();
        const tickets = useTableSeatingStore(state => state.tickets);

        const tableTickets = useMemo(() => {
            if (!normalizedId || !tickets) return [];
            return Object.values(tickets)
                .filter(t => t.seating_group_id === normalizedId)
                .sort((a, b) => (a.seat_number || 0) - (b.seat_number || 0));
        }, [tickets, normalizedId]);

        const ticketIds = useMemo(() => tableTickets.map(t => t.id), [tableTickets]);

        const seats = isUnseated ? [] : createSeatNumberArray(transformedGroup.capacity || 0);

        return (
            <div className={className} ref={forwardedRef} {...restProps}>
                {children || (
                    <Sortable.Root items={ticketIds}>
                        {isUnseated ? (
                            <>
                                {tableTickets.map((ticket, index) => (
                                    <SeatWithHeader
                                        key={ticket.id}
                                        seatNumber={ticket.seat_number || indexToSeatNumber(index)}
                                        prevSeatNumber={
                                            index > 0
                                                ? tableTickets[index - 1].seat_number ||
                                                  indexToSeatNumber(index - 1)
                                                : null
                                        }
                                    />
                                ))}
                                <div className="h-20 flex-shrink-0" />
                            </>
                        ) : (
                            seats.map(seatNum => (
                                <SeatWithHeader
                                    key={seatNum}
                                    seatNumber={seatNum}
                                    prevSeatNumber={seatNum > 1 ? seatNum - 1 : null}
                                />
                            ))
                        )}
                    </Sortable.Root>
                )}
            </div>
        );
    },
);
SeatingGroupCardSeats.displayName = 'SeatingGroupCardSeats';

interface SeatWithHeaderProps {
    seatNumber: number;
    prevSeatNumber: number | null;
}
const SeatWithHeader = ({ seatNumber, prevSeatNumber }: SeatWithHeaderProps) => {
    const { normalizedId } = useSeatingGroupCard();
    const seatData = useSeatDetails({ tableId: normalizedId, seatNumber });
    const prevSeatData = useSeatDetails({
        tableId: normalizedId,
        seatNumber: prevSeatNumber !== null ? prevSeatNumber : -1,
    });
    const { isGroupCollapsed } = useSelectionOperations();

    const currentGroupId = seatData?.ticket?.ticket_group_id;
    const prevGroupId = prevSeatNumber !== null ? prevSeatData?.ticket?.ticket_group_id : null;
    const ticketGroup = seatData?.ticketGroup;

    const isFirstInGroup = !prevGroupId || prevGroupId !== currentGroupId;
    const isCollapsed =
        normalizedId && currentGroupId ? isGroupCollapsed(currentGroupId, normalizedId) : false;
    const showHeader = isFirstInGroup && currentGroupId && ticketGroup;
    const showSeat = isFirstInGroup || !isCollapsed;

    return (
        <>
            {showHeader && (
                <SeatingGroupHeader
                    key={`group-header-${currentGroupId}-${seatNumber}`}
                    groupId={currentGroupId}
                    groupName={ticketGroup.name}
                    startSeatNumber={seatNumber}
                />
            )}
            <div key={seatNumber} className={showSeat ? '' : 'hidden'}>
                <SeatingGroupCardSeat seatNumber={seatNumber} />
            </div>
        </>
    );
};

interface SeatingGroupHeaderProps {
    groupId: string;
    groupName: string;
    startSeatNumber: number;
}
const SeatingGroupHeader = ({ groupId, groupName, startSeatNumber }: SeatingGroupHeaderProps) => {
    const { normalizedId } = useSeatingGroupCard();
    const tickets = useTableSeatingStore(state => state.tickets);

    const {
        selectedItems,
        showCheckboxes,
        toggleGroupSelection,
        isGroupCollapsed,
        toggleGroupCollapseAtTable,
    } = useSelectionOperations();

    const tableTickets = useMemo(() => {
        if (!normalizedId || !tickets) return [];
        return Object.values(tickets)
            .filter(t => t.seating_group_id === normalizedId)
            .sort((a, b) => (a.seat_number || 0) - (b.seat_number || 0));
    }, [tickets, normalizedId]);

    const isCollapsed = isGroupCollapsed(groupId, normalizedId || '');

    const groupMemberIds = useMemo(() => {
        if (!normalizedId) return [];
        return getContiguousGroupMembersFromSeat(groupId, normalizedId, {
            startSeatNumber,
            sortedTableTickets: tableTickets,
        });
    }, [groupId, normalizedId, startSeatNumber, tableTickets]);

    const allSelected = useMemo(() => {
        return groupMemberIds.length > 0 && groupMemberIds.every(id => selectedItems.has(id));
    }, [groupMemberIds, selectedItems]);

    const { attributes, listeners, setNodeRef, isDragging } = Draggable.useDraggable({
        id: `seated-group-${groupId}-${normalizedId}-${startSeatNumber}`,
        data: {
            type: DropTarget.GROUP,
            groupId,
            ticketIds: groupMemberIds,
        },
    });

    return (
        <div
            ref={setNodeRef}
            className={twMerge(
                'group mt-2 flex items-stretch rounded-t-md border-2 border-b-0 border-gray-300 bg-gray-100',
                'cursor-grab touch-none hover:cursor-grabbing',
                'text-sm',
            )}
            style={{ opacity: isDragging ? 0.5 : 1 }}
            {...attributes}
            data-group-drag-handle="true"
        >
            <div
                className="flex h-full min-h-0 w-8 items-center justify-center px-0 py-2"
                aria-label={`${showCheckboxes ? 'Select' : 'Drag'} ${groupName || 'group'}`}
            >
                <div className="flex h-5 w-5 items-center justify-center">
                    <Checkbox
                        className={twMerge(showCheckboxes ? 'block' : 'hidden group-hover:block')}
                        checked={allSelected}
                        onCheckedChange={() =>
                            toggleGroupSelection(groupId, normalizedId || '', startSeatNumber)
                        }
                        tabIndex={-1}
                        aria-label={`Select all tickets in ${groupName || 'group'}`}
                    />
                    <FontAwesomeIcon
                        icon={faGripDotsVertical}
                        className={twMerge(
                            'text-xs text-gray-400',
                            showCheckboxes ? 'hidden' : 'block group-hover:hidden',
                        )}
                        aria-hidden="true"
                    />
                </div>
            </div>
            <div
                className="flex flex-1 cursor-grab touch-none items-center gap-2 py-1.5 pr-2 active:cursor-grabbing"
                {...listeners}
            >
                <span className="font-medium">{groupName}</span>
                {groupMemberIds.length > 1 && (
                    <span className="text-xs text-gray-500">({groupMemberIds.length})</span>
                )}
            </div>
            {groupMemberIds.length > 1 ? (
                <Button
                    onClick={() => toggleGroupCollapseAtTable(groupId, normalizedId || '')}
                    variant="none"
                    size="sm"
                    shape="square"
                    aria-label={
                        isCollapsed ? `Expand ${groupName} group` : `Collapse ${groupName} group`
                    }
                >
                    <FontAwesomeIcon
                        icon={isCollapsed ? faChevronRight : faChevronDown}
                        className="text-gray-600"
                        size="xs"
                    />
                </Button>
            ) : (
                <div className="w-8" />
            )}
        </div>
    );
};

type RefCallback<T> = (instance: T | null) => void;
type RefObject<T> = { current: T | null };
type Ref<T> = RefCallback<T> | RefObject<T> | null | undefined;

const combineRefs = <T extends HTMLElement>(...refs: Ref<T>[]): RefCallback<T> => {
    return (node: T | null) => {
        refs.forEach(ref => {
            if (!ref) return;

            if (typeof ref === 'function') {
                ref(node);
                return;
            }

            ref.current = node;
        });
    };
};

const getOccupiedSeatClassNames = (
    isUnseated: boolean,
    hasGroup: boolean,
    hasSameGroupBelow: boolean,
    isOver: boolean,
    className?: string,
) => {
    return twMerge(
        'group flex touch-none items-center gap-2 pl-2 py-3 hover:bg-gray-50',
        !isUnseated && 'min-w-full',
        hasGroup
            ? [
                  'border-l-2 border-r-2 border-gray-300',
                  !hasSameGroupBelow ? 'border-b-2' : 'border-b',
                  'border-b-gray-300',
                  !hasSameGroupBelow ? 'mb-2 rounded-b-md' : '',
              ]
            : ['border-b border-dashed border-gray-200'],
        isOver && 'bg-yellow-100 ring-2 ring-yellow-400',
        className,
    );
};

interface SeatData {
    ticket: SeatingTicket;
    guestName: string;
    ticketGroup?: TicketGroup;
    groupPosition: GroupPosition;
    hasGroup: boolean;
    isFirstInGroup: boolean;
    isLastInGroup: boolean;
    ticketId: string;
    ticketGroupId: string | null;
}

interface AdjacentGroupInfoParams {
    ticket: SeatingTicket;
    normalizedId: string | undefined;
    isUnseated: boolean;
    seatNumber: number;
    nextSeatData: SeatData | null;
    group: TransformedSeatingGroup;
}

const getAdjacentGroupInfo = (params: AdjacentGroupInfoParams) => {
    const { ticket, nextSeatData } = params;

    return {
        hasSameGroupBelow: nextSeatData?.ticket?.ticket_group_id === ticket.ticket_group_id,
    };
};

interface OccupiedSeatProps extends ComponentPropsWithoutRef<'div'> {
    seatNumber: number;
}

const OccupiedSeat = forwardRef(
    (
        { className, seatNumber, ...restProps }: OccupiedSeatProps,
        forwardedRef: ForwardedRef<HTMLDivElement>,
    ) => {
        const { normalizedId, transformedGroup, isUnseated } = useSeatingGroupCard();
        const { showCheckboxes, toggleItemSelection, isSelected } = useSelectionOperations();

        const seatData = useSeatDetails({ tableId: normalizedId, seatNumber });
        const nextSeatData = useSeatDetails({
            tableId: normalizedId,
            seatNumber:
                transformedGroup.capacity == null || seatNumber < transformedGroup.capacity
                    ? seatNumber + 1
                    : -1,
        });

        const {
            attributes,
            listeners,
            setNodeRef: setSortableRef,
            transform,
            transition,
        } = Sortable.useSortable({
            id: seatData ? String(seatData.ticket.id) : `empty-${seatNumber}`,
            data: seatData
                ? {
                      type: DropTarget.TICKET,
                      ticketId: seatData.ticket.id,
                      ticketGroupId: seatData.ticket.ticket_group_id,
                      currentSeatingGroupId: seatData.ticket.seating_group_id,
                  }
                : undefined,
        });

        const { setNodeRef: setDroppableRef, isOver } = Draggable.useDroppable({
            id: seatData
                ? `${DropTarget.OCCUPIED_SEAT}-${seatData.ticket.id}`
                : `${DropTarget.EMPTY_SEAT}-${seatNumber}`,
            data: seatData
                ? {
                      type: DropTarget.OCCUPIED_SEAT,
                      ticketId: seatData.ticket.id,
                      tableId: seatData.ticket.seating_group_id,
                      seatNumber: seatData.ticket.seat_number,
                  }
                : undefined,
        });

        if (!seatData) return null;

        const { ticket, guestName, ticketGroup, hasGroup } = seatData;
        const itemSelected = isSelected(ticket.id);

        const { hasSameGroupBelow } = getAdjacentGroupInfo({
            ticket,
            normalizedId,
            isUnseated: isUnseated || false,
            seatNumber,
            nextSeatData,
            group: transformedGroup,
        });

        const style = {
            transform: Sortable.CSS.Transform.toString(transform),
            transition,
        };

        const groupInfo = hasGroup ? ', ' + (ticketGroup?.name || 'group member') : '';
        const ariaLabel = `${ticket.first_name} ${ticket.last_name} - Seat ${seatNumber}${groupInfo}`;

        return (
            <div
                ref={combineRefs(setSortableRef, setDroppableRef, forwardedRef)}
                style={style}
                className={getOccupiedSeatClassNames(
                    isUnseated || false,
                    hasGroup,
                    hasSameGroupBelow,
                    isOver,
                    className,
                )}
                aria-label={ariaLabel}
                {...attributes}
                {...listeners}
                {...restProps}
            >
                <div className="flex h-full w-full">
                    <div className="-ml-2 flex h-full min-h-0 w-8 items-center justify-center px-0 py-3">
                        <div className="seat-control-container flex h-5 w-5 items-center justify-center">
                            <Checkbox
                                className={twMerge(
                                    showCheckboxes ? 'block' : 'hidden group-hover:block',
                                )}
                                checked={itemSelected}
                                onCheckedChange={() => toggleItemSelection(ticket.id)}
                                aria-label={`Select ${ticket.first_name} ${ticket.last_name}`}
                                tabIndex={-1}
                            />
                            <div
                                className={twMerge(
                                    'cursor-grab touch-none active:cursor-grabbing',
                                    showCheckboxes ? 'hidden' : 'block group-hover:hidden',
                                )}
                            >
                                <FontAwesomeIcon
                                    icon={faGripDotsVertical}
                                    className="text-gray-400"
                                    aria-hidden="true"
                                />
                            </div>
                        </div>
                    </div>
                    {!isUnseated && (
                        <span className="flex w-8 flex-shrink-0 cursor-grab touch-none items-center justify-center text-sm font-medium text-gray-600 active:cursor-grabbing">
                            {seatNumber}
                        </span>
                    )}
                    <div
                        {...listeners}
                        className="flex min-w-0 flex-1 cursor-grab touch-none active:cursor-grabbing"
                    >
                        <div className="flex flex-1 flex-col">
                            <div className="flex items-center gap-2">
                                <span className="truncate text-sm font-medium text-black">
                                    {guestName}
                                </span>
                                {hasGroup && (
                                    <FontAwesomeIcon
                                        icon={faPeopleGroup}
                                        className="text-brand-violet-primary flex-shrink-0 text-xs"
                                        aria-hidden="true"
                                    />
                                )}
                            </div>
                            {ticket.title && (
                                <span className="truncate text-xs text-gray-600">
                                    {ticket.title}
                                </span>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    },
);
OccupiedSeat.displayName = 'OccupiedSeat';

interface EmptySeatProps extends ComponentPropsWithoutRef<'div'> {
    seatNumber: number;
}
const EmptySeat = forwardRef(
    (
        { className, seatNumber, ...restProps }: EmptySeatProps,
        forwardedRef: ForwardedRef<HTMLDivElement>,
    ) => {
        const { normalizedId } = useSeatingGroupCard();
        const { removeSeatByNumber } = useTableOperations(normalizedId || '');
        const { setNodeRef, isOver } = Draggable.useDroppable({
            id: `seat-${normalizedId}-${seatNumber}`,
            data: {
                type: DropTarget.EMPTY_SEAT,
                tableId: normalizedId,
                seatNumber: seatNumber,
            },
        });

        return (
            <div
                className={twMerge(
                    'group relative flex min-w-full items-center gap-3 border-b border-dashed border-gray-200 px-2 py-5 hover:border-y hover:border-gray-300 hover:bg-gray-50',
                    isOver && 'bg-blue-100 ring-2 ring-blue-400',
                    className,
                )}
                ref={node => {
                    setNodeRef(node);
                    if (forwardedRef) {
                        if (typeof forwardedRef === 'function') {
                            forwardedRef(node);
                        } else {
                            forwardedRef.current = node;
                        }
                    }
                }}
                {...restProps}
            >
                <span className="w-4 text-xs font-medium text-gray-600">{seatNumber}</span>
                <Button
                    variant="transparent"
                    color="neutral"
                    size="sm"
                    shape="square"
                    className="invisible group-hover:visible"
                    aria-label={`Search for guest to seat at seat ${seatNumber}`}
                >
                    <FontAwesomeIcon
                        icon={faMagnifyingGlass}
                        className="text-xs text-gray-500 hover:text-gray-700"
                    />
                </Button>
                <span className="flex-1 text-sm text-gray-400 group-hover:text-gray-700">
                    Seat {seatNumber}
                </span>
                <Button
                    variant="transparent"
                    color="neutral"
                    className="invisible group-hover:visible"
                    onClick={() => removeSeatByNumber(seatNumber)}
                    disabled={!normalizedId}
                    aria-label={`Remove seat ${seatNumber}`}
                >
                    <FontAwesomeIcon
                        icon={faTrashCan}
                        className="text-xs text-gray-500 hover:text-red-600"
                    />
                </Button>
            </div>
        );
    },
);
EmptySeat.displayName = 'EmptySeat';

interface SeatingGroupCardSeatProps extends ComponentPropsWithoutRef<'div'> {
    seatNumber: number;
}
const SeatingGroupCardSeat = forwardRef(
    (
        { seatNumber, ...props }: SeatingGroupCardSeatProps,
        forwardedRef: ForwardedRef<HTMLDivElement>,
    ) => {
        const { normalizedId } = useSeatingGroupCard();
        const tickets = useTableSeatingStore(state => state.tickets);

        const isOccupied = Object.values(tickets).some(
            t => t.seating_group_id === normalizedId && t.seat_number === seatNumber,
        );

        if (isOccupied) {
            return <OccupiedSeat ref={forwardedRef} seatNumber={seatNumber} {...props} />;
        }
        return <EmptySeat ref={forwardedRef} seatNumber={seatNumber} {...props} />;
    },
);
SeatingGroupCardSeat.displayName = 'SeatingGroupCardSeat';

interface SeatingGroupCardFooterProps extends ComponentPropsWithoutRef<'div'> {}
const SeatingGroupCardFooter = forwardRef(
    (
        { className, children, ...restProps }: SeatingGroupCardFooterProps,
        forwardedRef: ForwardedRef<HTMLDivElement>,
    ) => {
        const { normalizedId } = useSeatingGroupCard();
        const { addSeat } = useTableOperations(normalizedId || '');

        return (
            <div className={twMerge('pt-3', className)} ref={forwardedRef} {...restProps}>
                {children || (
                    <Button
                        variant="transparent"
                        className="flex w-full items-center justify-center gap-2 text-blue-700 hover:bg-blue-50"
                        size="sm"
                        onClick={addSeat}
                        disabled={!normalizedId}
                    >
                        <FontAwesomeIcon icon={faPlus} className="text-blue-700" />
                        <p>Add seat</p>
                    </Button>
                )}
            </div>
        );
    },
);
SeatingGroupCardFooter.displayName = 'SeatingGroupCardFooter';

const Root = SeatingGroupCardContainer;
const Inner = SeatingGroupCardInner;
const Header = SeatingGroupCardHeader;
const Seats = SeatingGroupCardSeats;
const Seat = SeatingGroupCardSeat;
const Footer = SeatingGroupCardFooter;

export {
    SeatingGroupCardContainer,
    SeatingGroupCardInner,
    SeatingGroupCardHeader,
    SeatingGroupCardSeats,
    SeatingGroupCardSeat,
    SeatingGroupCardFooter,
    //
    Root,
    Inner,
    Header,
    Seats,
    Seat,
    Footer,
};
